import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import { JustupColors } from "../../../../shared/enums/justup-colors.enum";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { ensureChartJs } from "../../../articles/services/chart.utils";
import {
  groupBy,
  sum,
  toDayKey,
  wAvg,
} from "../../../articles/services/stats.util";
import { ReceptionCommande } from "../../../reception-commande/models/reception-commande.model";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { SelectAutocompleteComponent } from "../../../../shared/modules/select-autocomplete/select-autocomplete.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { TiersFacadeService } from "../../services/tiers/tiers-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { LocalStorageService } from "../../../../shared/services/local-storage.service";
import {
  AchatEvolutionCommandeRow,
  AchatEvolutionDialogData,
  AchatEvolutionLot,
  StatsAchatEvolutionDialogComponent,
} from "./stats-achat-evolution-dialog.component";

type OrderPoint = {
  key: string;
  numero: string;
  dateKey: string;
  label: string;
  pu: number;
  qty: number;
};

@Component({
  selector: "vl-stats-achat-evolution-tiers",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    SharedModule,
    ChipsComponent,
    PopoverModule,
    SelectAutocompleteComponent,
    ChipsToggleComponent,
  ],
  templateUrl: "./stats-article-achat-evolution-tiers.component.html",
  styleUrls: ["./stats-article-achat-evolution-tiers.component.scss"],
  host: { class: "widget-component" },
})
export class StatsAchatEvolutionByArticleComponent implements OnDestroy {
  private readonly tiersFacade = inject(TiersFacadeService);
  stats = this.tiersFacade.getState().stats;

  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  articleInfosFacade = inject(ArticlesInfosFacadeService);
  private dialogsService = inject(DialogsService);
  private localStorage = inject(LocalStorageService);

  private readonly STORAGE_KEY = "justup-widget-achat-evolution";

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("articleAchat");
  private chart?: Chart;

  chartMode = signal<"pu" | "ca">("pu");
  chartModeElements = [
    { name: "PU", value: "pu" as const },
    { name: "CA", value: "ca" as const },
  ];

  selectedArticleId = signal<string>("");

  articlesOptions = computed<{ name: string; value: string }[]>(() => {
    if (this.stats()?.tiers?.articlesFournis?.length) {
      return this.stats()?.tiers?.articlesFournis?.map((af) => ({
        name: this.articleInfosFacade.getByArticleId(af.articleId)?.()
          ?.denomination,
        value: af.articleId,
      }));
    }

    return [];
  });

  constructor() {
    effect(
      () => {
        const opts = this.articlesOptions();
        untracked(() => {
          const sel = this.selectedArticleId();
          if (opts.find((o) => o.value === sel)) return;
          const saved = this.localStorage.getWithTTL(`${this.STORAGE_KEY}-article`) as string;
          const pick =
            (saved && opts.find((o) => o.value === saved)?.value) ||
            opts[0]?.value || "";
          if (pick !== sel) this.selectedArticleId.set(pick);
        });
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        this.stats();
        this.selectedArticleId();
        const max = this.maxRows();
        this.topCount.set(max || 1);
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const id = this.selectedArticleId();
      if (id) this.localStorage.putWithTTL(`${this.STORAGE_KEY}-article`, id);
    });

    effect(
      () => {
        ensureChartJs();
        this.chartMode();
        if (this.canvasRef() && this.pointsTop().length) {
          this.buildChart();
        } else {
          this.destroyChart();
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => this.updateChart(), { allowSignalWrites: true });

    effect(
      () => {
        const max = this.maxRows();
        const cur = this.topCount();
        const clamped = Math.min(Math.max(1, cur), Math.max(1, max));
        if (clamped !== cur) this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );
  }

  topCount = signal<number>(20);
  search = signal<string>("");

  private filteredReceptions = computed<ReceptionCommande[]>(() => {
    const recs = this.stats()?.receptions ?? [];
    const sel = this.selectedArticleId();
    if (!sel) return [];
    return recs.filter((r) => String(r.commandeLigne.articleId) === sel);
  });

  private pointsAll = computed<OrderPoint[]>(() => {
    const rows = this.filteredReceptions();
    if (!rows.length) return [];

    const byOrder = groupBy(rows, (r: any) => r?.commandeId);

    const fmtFR = new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    });

    const list: OrderPoint[] = Object.keys(byOrder).map((key) => {
      const arr = byOrder[key] ?? [];
      const prix = arr.map((r: any) => parseNumber(r?.prixUnitaire ?? 0));
      const qtes = arr.map((r: any) => parseNumber(r?.quantite ?? 0));

      const dates = arr.map((r: any) => toDayKey(r?.date));
      const dateKey = dates.sort().at(-1) ?? toDayKey(arr[0]?.date);

      const numero = this.getReceptionNumero(arr[0], key);

      const pu = wAvg(prix, qtes) ?? 0;
      const qty = sum(qtes) ?? 0;

      const labelDate = (() => {
        try {
          return fmtFR.format(new Date(dateKey));
        } catch {
          return dateKey;
        }
      })();
      const label = `${labelDate} • ${numero}`;

      return { key, numero, dateKey, label, pu, qty };
    });

    return list.sort((a, b) =>
      a.dateKey === b.dateKey
        ? a.numero.localeCompare(b.numero)
        : a.dateKey.localeCompare(b.dateKey),
    );
  });

  private pointsFiltered = computed<OrderPoint[]>(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.pointsAll();
    return this.pointsAll().filter((p) => p.numero.toLowerCase().includes(q));
  });
  maxRows = computed(() => this.pointsFiltered().length);

  pointsTop = computed<OrderPoint[]>(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    const arr = this.pointsFiltered();
    return arr.slice(Math.max(0, arr.length - n));
  });

  summaryLabel = computed<string | null>(() => {
    const points = this.pointsAll();
    if (!points.length) return null;
    let qty = 0, cout = 0;
    for (const p of points) {
      qty += p.qty;
      cout += p.pu * p.qty;
    }
    if (qty <= 0) return null;
    const pu = cout / qty;
    return `PU moy. ${pu.toFixed(2)} €`;
  });

  selectedArticleDenomination = computed<string>(() => {
    const id = this.selectedArticleId();
    if (!id) return "";
    return this.articleInfosFacade.getByArticleId(id)?.()?.denomination ?? id;
  });

  private labels = computed<string[]>(() =>
    this.pointsTop().map((p) => p.label),
  );

  private barDataset = computed(() => {
    const data = this.pointsTop().map((p) => p.pu);
    return {
      type: "bar" as const,
      label: "PU par commande",
      data,
      backgroundColor: JustupColors.BLEU_CLAIR,
      borderColor: JustupColors.BLEU,
      borderWidth: 1,
      maxBarThickness: 36,
      order: 2,
    };
  });

  private avgDataset = computed(() => {
    const pts = this.pointsTop();
    let accPV = 0;
    let accQ = 0;
    const data = pts.map((p) => {
      accPV += (p.pu ?? 0) * (p.qty ?? 0);
      accQ += p.qty ?? 0;
      return accQ > 0 ? accPV / accQ : 0;
    });
    return {
      type: "line" as const,
      label: "Moyenne pondérée cumulée",
      data,
      borderColor: JustupColors.VIOLET,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.2,
      fill: false,
      order: 1,
    };
  });

  private dsCostBar = computed(() => ({
    type: "bar" as const,
    label: "Coût total",
    data: this.pointsTop().map((p) => p.pu * p.qty),
    maxBarThickness: 36,
    order: 1,
  }));

  openFullList() {
    const rows = this.filteredReceptions();
    if (!rows.length) return;

    const articleInfo = this.articleInfosFacade.getByArticleId(this.selectedArticleId())?.();
    const uniteGestion = (articleInfo?.unite as any)?.denomination || "UG";
    const tiersDenomination = this.stats()?.tiers?.denomination ?? "";

    const byCommande = groupBy(rows, (r: any) => r?.commandeId);

    const commandes: AchatEvolutionCommandeRow[] = Object.keys(byCommande).map((key) => {
      const arr = byCommande[key] ?? [];
      const first: any = arr[0] ?? {};
      const numero = this.getReceptionNumero(first, key);
      const dateKey =
        arr
          .map((r: any) => toDayKey(r?.date))
          .filter((d: string) => !!d)
          .sort()
          .at(-1) ?? "";

      let qtyUG = 0, coutTotal = 0;

      const byLot = new Map<string, {
        lotId: string | null;
        qtyUG: number;
        coutTotal: number;
        puSum: number;
        puCount: number;
        lotNumero: string;
      }>();

      for (const r of arr as any[]) {
        const qty = parseNumber(r?.quantite ?? 0);
        const pu = parseNumber(r?.prixUnitaire ?? 0);

        qtyUG += qty;
        coutTotal += qty * pu;

        const lotIdRaw: string | null = r?.lotId || r?.lot?.id || null;
        const lotKey = lotIdRaw || `__nolot_${r?.lot?.numero ?? "?"}`;
        const lotNumero = r?.lot?.numero ?? "";
        if (!byLot.has(lotKey)) {
          byLot.set(lotKey, {
            lotId: lotIdRaw,
            qtyUG: 0, coutTotal: 0,
            puSum: 0, puCount: 0,
            lotNumero,
          });
        }
        const l = byLot.get(lotKey)!;
        l.qtyUG += qty;
        l.coutTotal += qty * pu;
        if (qty > 0) {
          l.puSum += pu;
          l.puCount++;
        }
      }

      const lots: AchatEvolutionLot[] = [...byLot.values()].map((v) => {
        const puAchat = v.qtyUG > 0 ? v.coutTotal / v.qtyUG : 0;
        return {
          lotId: v.lotId,
          lotNumero: v.lotNumero,
          qtyUG: v.qtyUG,
          uniteGestion,
          puAchat,
          coutTotal: v.coutTotal,
        };
      }).sort((a, b) => b.qtyUG - a.qtyUG);

      const puAchatMoyen = qtyUG !== 0 ? coutTotal / qtyUG : 0;

      return {
        commandeId: String(key),
        numero,
        dateKey,
        qtyUG,
        puAchatMoyen,
        coutTotal,
        lots,
      };
    });

    const data: AchatEvolutionDialogData = {
      articleDenomination: this.selectedArticleDenomination(),
      articleUniteGestion: uniteGestion,
      tiersDenomination,
      tiersId: this.stats()?.tiers?.id,
      articleId: this.selectedArticleId() || undefined,
      commandes,
    };

    this.dialogsService.open(
      StatsAchatEvolutionDialogComponent,
      "min(95vw, 860px)",
      "fit-content",
      data,
    );
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private buildChart(): void {
    const canvas = this.canvasRef();
    const ctx = canvas?.nativeElement.getContext("2d");
    if (!ctx) return;

    const css = getComputedStyle(canvas!.nativeElement);
    const BG_ALT = (css.getPropertyValue("--color-on-background-alt") || "#476373").trim();
    const OUTLINE = (css.getPropertyValue("--color-outline") || "#9db8c3").trim();
    const ACCENT = (css.getPropertyValue("--color-accent") || "#c535ff").trim();
    const ON_ACCENT = (css.getPropertyValue("--color-on-accent") || "#ffffff").trim();
    const ON_ACCENT_ALT = (css.getPropertyValue("--color-on-accent-alt") || "rgba(197,53,255,.35)").trim();

    this.destroyChart?.();

    const mode = this.chartMode();
    let datasets: any[];
    let tooltipCallbacks: any;

    if (mode === "ca") {
      const costBar = { ...(this.dsCostBar() as any) };
      costBar.backgroundColor = ON_ACCENT_ALT;
      costBar.borderColor = "transparent";
      costBar.borderWidth = 0;

      datasets = [costBar];
      tooltipCallbacks = {
        title: (items: any[]) => items?.[0]?.label ?? "",
        label: (ctx: any) => {
          const idx = ctx.dataIndex ?? 0;
          const p = (this as any).pointsTop?.()[idx];
          const eur = (v: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v ?? 0);
          return `Coût: ${eur(ctx.parsed.y)}  •  Qté: ${p?.qty ?? 0}`;
        },
      };
    } else {
      const bar = { ...(this.barDataset() as any) };
      bar.backgroundColor = ON_ACCENT_ALT;
      bar.borderColor = "transparent";
      bar.borderWidth = 0;
      bar.maxBarThickness = bar.maxBarThickness ?? 36;

      const line = { ...(this.avgDataset() as any) };
      line.borderColor = ACCENT;
      line.backgroundColor = "rgba(0,0,0,0)";
      line.borderWidth = 5;
      line.pointBackgroundColor = ACCENT;
      line.pointBorderColor = ON_ACCENT;
      line.pointBorderWidth = 3;
      line.pointRadius = 7;

      datasets = [bar, line];
      tooltipCallbacks = {
        title: (items: any[]) => items?.[0]?.label ?? "",
        label: (ctx: any) => {
          const idx = ctx.dataIndex ?? 0;
          const p = (this as any).pointsTop?.()[idx];
          const eur = (v: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 4 }).format(v ?? 0);
          if (ctx.dataset.type === "bar") {
            return `PU commande: ${eur(ctx.parsed.y)}  •  Qté: ${p?.qty ?? 0}`;
          }
          return `Moyenne pondérée cumulée: ${eur(ctx.parsed.y)}`;
        },
      };
    }

    this.chart = new Chart(ctx, {
      type: "bar",
      data: { labels: this.labels(), datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: tooltipCallbacks },
        },
        scales: {
          y: {
            grid: { color: OUTLINE },
            border: { color: OUTLINE },
            ticks: {
              color: BG_ALT,
              callback: (v) =>
                new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: mode === "ca" ? 0 : 2 }).format(parseNumber(v)),
            },
          },
          x: {
            grid: { display: false },
            border: { color: OUTLINE },
            ticks: { color: BG_ALT, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 },
          },
        },
        elements: {
          line: { borderWidth: 2 },
          point: { hitRadius: 8, hoverRadius: 4 },
        },
        layout: { padding: { left: 6, right: 12, top: 6, bottom: 6 } },
      },
    });
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.data.labels = this.labels() as any;

    if (this.chartMode() === "ca") {
      const costBar = this.dsCostBar();
      if ((this.chart.data.datasets?.length ?? 0) >= 1) {
        const d0 = this.chart.data.datasets[0] as any;
        d0.data = costBar.data;
      }
    } else {
      const bar = this.barDataset();
      const line = this.avgDataset();
      if ((this.chart.data.datasets?.length ?? 0) >= 2) {
        const d0 = this.chart.data.datasets[0] as any;
        d0.data = bar.data;
        const d1 = this.chart.data.datasets[1] as any;
        d1.data = line.data;
      }
    }
    this.chart.update("none");
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private getReceptionNumero(
    r: ReceptionCommande,
    fallbackKey: string,
  ): string {
    return r?.commandeLigne?.commande?.numero ?? String(fallbackKey ?? "");
  }

  protected readonly Math = Math;
}
